import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ActionBoardService, OutstandingSummary} from './action-board.service';
import {MessageBusService} from '../message-bus.service';

export const OPEN_ACTION_BOARD_REQUEST_TOPIC = 'ActionBoard.OpenBoardRequest';

/**
 * CR-20260922-001 — the footer badge: "ACTION BOARD · 3 outstanding · 1 overdue". The pinned-first-tab
 * of the spec has no API in the workspace (tabs are a plain ordered array); this badge is the
 * always-visible way in — one click opens or activates the Action Board panel in the current layout.
 * The count on the panel's own tab header is not shown (tab headers are user-renamed text).
 */
@Component({
    selector: 'ets-action-board-footer-badge',
    template: `
        <div class="ab-badge" [class.red]="summary?.highest === 'red'" [class.orange]="summary?.highest === 'orange'"
             [class.yellow]="summary?.highest === 'yellow'" [class.clear]="!summary || summary.outstanding === 0"
             (click)="open()" title="Open the Action Board">
            <span class="label">ACTION BOARD</span>
            <span class="count" *ngIf="summary && summary.outstanding > 0">{{ summary.outstanding }} outstanding</span>
            <span class="overdue" *ngIf="summary && summary.overdue > 0">· {{ summary.overdue }} overdue</span>
            <span class="count" *ngIf="!summary || summary.outstanding === 0">clear</span>
        </div>
    `,
    styles: [`
        .ab-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 2px 8px;
            border-radius: 3px;
            cursor: pointer;
            font-size: 11px;
            letter-spacing: 0.3px;
            border: 1px solid #555;
            color: #ddd;
            user-select: none;
        }

        .ab-badge .label {
            font-weight: 600;
        }

        .ab-badge.clear {
            opacity: 0.6;
        }

        .ab-badge.red {
            background: #7f1d1d;
            border-color: #ef4444;
            color: #fff;
        }

        .ab-badge.orange {
            background: #7c2d12;
            border-color: #f59e0b;
            color: #fff;
        }

        .ab-badge.yellow {
            background: #713f12;
            border-color: #facc15;
            color: #fff;
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActionBoardFooterBadgeComponent implements OnInit, OnDestroy {

    constructor(
        private readonly _service: ActionBoardService,
        private readonly _messageBus: MessageBusService,
        private readonly _changeDetector: ChangeDetectorRef
    ) {
    }

    private readonly _unsubscriber = new Subject<void>();

    summary: OutstandingSummary | null = null;

    ngOnInit(): void {
        this._service.ownSummary$
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(s => {
                this.summary = s;
                this._changeDetector.detectChanges();
            });

        // The login-time board read moved to the overlay component when this badge was hidden
        // (owner 2026-09-22). If the badge returns, the overlay still does that read.
    }

    ngOnDestroy(): void {
        this._unsubscriber.next();
        this._unsubscriber.complete();
    }

    open(): void {
        this._messageBus.publish({topic: OPEN_ACTION_BOARD_REQUEST_TOPIC, payload: {at: Date.now()}});
    }
}
